import { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MentionTextarea, renderWithMentions } from "@/components/form/MentionTextarea";
import { toast } from "sonner";
import { Pencil, Trash2, Loader2, Pin, PinOff, ArrowRight } from "lucide-react";
import { Avatar } from "@/components/shared/UI";

function whenLabel(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "pm" : "am";
  const hr = ((h + 11) % 12) + 1;
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} · ${hr}:${m}${ampm}`;
}

const sortNotes = (list) => [...list].sort((a, b) =>
  (b.pinned_at || "").localeCompare(a.pinned_at || "") || (b.created_at || "").localeCompare(a.created_at || ""));

export function NotesPanel({ entityType, entityId, legacyNote, migratePath }) {
  const { users } = useAuth();
  const [notes, setNotes] = useState([]);
  const [legacy, setLegacy] = useState(legacyNote);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = useCallback(() => {
    client.get("/notes", { params: { entity_type: entityType, entity_id: entityId } }).then(({ data }) => setNotes(data));
  }, [entityType, entityId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setLegacy(legacyNote); }, [legacyNote]);

  // Migrate a legacy free-text note into a real, editable/deletable note.
  useEffect(() => {
    if (!legacy || !migratePath) return;
    let cancelled = false;
    client.post(migratePath).then(({ data }) => {
      if (cancelled) return;
      if (data) load();
      setLegacy(null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [legacy, migratePath, load]);

  const add = async () => {
    const t = text.trim();
    if (!t || saving) return;
    setSaving(true);
    try {
      const { data } = await client.post("/notes", { entity_type: entityType, entity_id: entityId, text: t });
      setNotes((n) => sortNotes([data, ...n]));
      setText("");
      toast.success("Note added");
    } catch (e) {
      toast.error("Could not save note");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditText(n.text); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async (id) => {
    const t = editText.trim();
    if (!t) return;
    try {
      const { data } = await client.put(`/notes/${id}`, { text: t });
      setNotes((n) => n.map((x) => (x.id === id ? data : x)));
      cancelEdit();
      toast.success("Note updated");
    } catch (e) {
      toast.error("Could not update note");
    }
  };

  const togglePin = async (n) => {
    try {
      const { data } = await client.put(`/notes/${n.id}/pin`, { pinned: !n.pinned_at });
      setNotes((list) => sortNotes(list.map((x) => (x.id === n.id ? data : x))));
      toast.success(data.pinned_at ? "Note pinned" : "Note unpinned");
    } catch (e) {
      toast.error("Could not update pin");
    }
  };

  const del = async (id) => {
    try {
      await client.delete(`/notes/${id}`);
      setNotes((n) => n.filter((x) => x.id !== id));
      toast.success("Note deleted");
    } catch (e) {
      toast.error("Could not delete note");
    }
  };

  return (
    <div>
      <div className="relative">
        <MentionTextarea
          testid="note-input"
          className="pr-11"
          value={text}
          onChange={setText}
          users={users}
          onBlur={add}
          onEnter={add}
          rows={2}
          placeholder="Add a note… use @ to mention a teammate"
        />
        {saving ? (
          <Loader2 className="absolute bottom-3 right-3 h-4 w-4 animate-spin text-sky-400" />
        ) : text.trim() && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={add}
            data-testid="note-save-button"
            title="Save note"
            className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-sky-600 text-white shadow-sm transition-transform hover:scale-105 hover:bg-sky-700"
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mt-3 space-y-2" data-testid="notes-list">
        {notes.length === 0 && !legacy && (
          <p className="px-1 py-2 text-sm text-slate-400">No notes yet.</p>
        )}
        {notes.map((n) => (
          <div key={n.id} data-testid={`note-item-${n.id}`} className={`group relative rounded-lg border p-3 ${n.pinned_at ? "border-amber-200 bg-amber-50/50" : "border-slate-100"}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <Avatar name={n.created_by_name} user className="h-6 w-6 shrink-0 text-[9px]" />
                <span className="whitespace-nowrap text-xs font-semibold text-slate-700">{n.created_by_name || "Unknown"}</span>
                <span className="whitespace-nowrap text-xs text-slate-400">{whenLabel(n.created_at)}</span>
                {n.edited_at && <span className="text-[10px] italic text-slate-300">(edited)</span>}
                {n.pinned_at && (
                  <span data-testid={`note-pinned-badge-${n.id}`} className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                    <Pin className="h-2.5 w-2.5" /> Pinned
                  </span>
                )}
              </div>
              {editingId === n.id ? (
                <div className="mt-1.5">
                  <MentionTextarea value={editText} onChange={setEditText} users={users} rows={2} testid={`note-edit-input-${n.id}`} onEnter={() => saveEdit(n.id)} />
                  <div className="mt-1.5 flex items-center gap-2">
                    <button onClick={() => saveEdit(n.id)} data-testid={`note-edit-save-${n.id}`} className="rounded-md bg-sky-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-sky-700">Save</button>
                    <button onClick={cancelEdit} data-testid={`note-edit-cancel-${n.id}`} className="rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100">Cancel</button>
                  </div>
                </div>
              ) : (
                <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-slate-700">{renderWithMentions(n.text, users)}</p>
              )}
            </div>
            {editingId !== n.id && (
              <div className="pointer-events-none absolute right-2 top-2 flex items-center gap-0.5 rounded-md border border-slate-100 bg-white/95 p-0.5 opacity-0 shadow-sm transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 focus-within:pointer-events-auto focus-within:opacity-100">
                <button
                  onClick={() => togglePin(n)}
                  data-testid={`note-pin-${n.id}`}
                  className={`rounded-md p-1 hover:bg-amber-50 hover:text-amber-600 ${n.pinned_at ? "text-amber-500" : "text-slate-300"}`}
                  title={n.pinned_at ? "Unpin note" : "Pin note"}
                >
                  {n.pinned_at ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                </button>
                <button
                  onClick={() => startEdit(n)}
                  data-testid={`note-edit-${n.id}`}
                  className="rounded-md p-1 text-slate-300 hover:bg-slate-100 hover:text-sky-600"
                  title="Edit note"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => del(n.id)}
                  data-testid={`note-delete-${n.id}`}
                  className="rounded-md p-1 text-slate-300 hover:bg-red-50 hover:text-red-600"
                  title="Delete note"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
        {legacy && (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-3">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Earlier note</p>
            <p className="whitespace-pre-wrap text-sm text-slate-600">{legacy}</p>
          </div>
        )}
      </div>
    </div>
  );
}
